import React from 'react';
import { Box, Typography, Divider, Tooltip } from '@mui/material';
import { ArrowForwardIosRounded, ErrorOutlineRounded } from '@mui/icons-material';
import { Stack } from '@mui/system';
import { theme } from '../../../theme';
import { NumericFormat } from 'react-number-format';

// Scrollable cannot be true on the physician dashboard since it will scale the graph incorrectly and the relative bar sizes of the physician vs peers will be off
function PhysicianBars({ cards, height = 30, maxTotal, isPhysician = false, categoryColorMap, isFiltered = false, handleBarClick = null, scrollable = false, selectedPhysicians = [] }) {
    // Check to see if no cards have entries data
    const hasEntriesData = cards.some(card => Array.isArray(card.entries) && card.entries.length > 0);

    if (!hasEntriesData && !isPhysician && !isFiltered) {
        return (
            <Typography variant="normal" alignSelf={'center'} fontSize={'14px'} color={theme.palette.text.secondary}>
                Insufficient Physician Data
            </Typography>
        );
    }

    return (
        <Stack 
            spacing={0.25}
            className={isPhysician ? 'pendo-physician-bar' : 'pendo-peer-bars'} 
            sx={{ 
                overflowY: scrollable ? 'auto' : 'visible',
                '& > *': { flexShrink: 0 } // Prevent shrinking of child elements
            }}
        >
            {cards.map((card) => {
                const entries = Array.isArray(card.entries) ? card.entries : [];
                const total = entries.reduce((sum, [, val]) => sum + val, 0);
                
                const isSelected = selectedPhysicians.includes(card.physician_id);
                
                return (
                    <>
                        <Stack
                            onClick={isPhysician ? null : () => handleBarClick(card.physician_id)}
                            sx={{
                                backgroundColor: isSelected ? theme.palette.action.selected : 'transparent',
                                borderRadius: 1,
                                border: isSelected ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                                '&:hover': {
                                    backgroundColor: isPhysician ? 'transparent' : theme.palette.action.hover,
                                },
                                cursor: isPhysician ? 'default' : 'pointer',
                                transition: 'all 0.2s ease-in-out',
                            }}
                        >
                            {total === 0 ? (
                                <Stack direction="row" alignItems="center" sx={{ minHeight: height }}>
                                    <Typography variant="normal" fontSize={'14px'} color={theme.palette.text.secondary} noWrap>
                                        {card.physician_first_name?.charAt(0)}. {card.physician_last_name} - Insufficient Data 
                                        <ErrorOutlineRounded sx={{ fontSize: '14px', verticalAlign: 'middle', ml: 0.5 }} />
                                    </Typography>
                                </Stack>
                            ) : 
                                <PhysicianBar 
                                    card={card} 
                                    barWidthPercent={maxTotal ? (total / maxTotal) * 80 : 80} 
                                    height={height} 
                                    categoryColorMap={categoryColorMap}
                                    entries={entries}
                                    isPhysician={isPhysician}
                                    total={total}
                                />
                            }
                        </Stack>
                        {!isPhysician && <Divider />}
                    </>
                );
            })}
        </Stack>
    );
}

export default PhysicianBars;

function PhysicianBar({ card, barWidthPercent, height, categoryColorMap, entries, isPhysician = false, total }) {
    return (
        <Stack direction="row" sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Box
                sx={{
                    display: 'flex',
                    width: `${barWidthPercent}%`,
                    height,
                    borderRadius: 1,
                    overflow: 'hidden',
                }}
            >
                {entries.map(([cat, val]) => (
                    <Tooltip 
                        variant="white"
                        key={cat}
                        title={
                            <Stack direction={'row'} p={1} spacing={1} sx={{ display: 'flex', alignItems: 'center'}}>
                                <Box 
                                    sx={{ 
                                        width: 12, 
                                        height: 12, 
                                        bgcolor: categoryColorMap[cat], 
                                        borderRadius: 0.5 
                                    }} 
                                />
                                <Typography variant="normal" fontSize="12px">
                                    {cat}
                                </Typography>
                                <NumericFormat
                                    value={val/100}
                                    displayType={'text'}
                                    thousandSeparator={true}
                                    prefix={'$'}
                                    decimalScale={2}
                                    fixedDecimalScale={true}
                                />
                            </Stack>
                        }
                        arrow
                        placement="bottom"
                    >
                        <Box
                            tabIndex={0}
                            role="button"
                            aria-label={`${cat}: $${(val/100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            sx={{
                                width: `${total === 0 ? 0 : (val / total) * 100}%`,
                                bgcolor: categoryColorMap[cat],
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        />
                    </Tooltip>
                ))}
            </Box>
            {/* last_name and total */}
            <Stack
                pl={0.5}
                maxWidth={'20%'}
                textAlign='left'
                sx={{ minWidth: 0, overflow: 'hidden', flexShrink: 1 }}
            >
                <Typography variant='normal' fontSize={'12px'}>
                    <NumericFormat
                        value={total/100}
                        displayType={'text'}
                        thousandSeparator={true}
                        prefix={'$'}
                        decimalScale={2}
                    />
                </Typography>
                <Typography variant='normal' fontSize={'12px'} noWrap sx={{ minWidth: 0, maxWidth: '100%', flexShrink: 1 }}>
                    {card.physician_first_name.charAt(0)}. {card.physician_last_name}
                </Typography>
            </Stack>
            {/* Arrow anchored to far right of parent row */}
            {!isPhysician && (
                <ArrowForwardIosRounded sx={{ fontSize: 'small', color: theme.palette.subtext.main, ml: 'auto' }} />
            )}
        </Stack>
    );
}